import React from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Button,
    Typography,
    makeStyles,
} from '@material-ui/core'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import AddIcon from '@material-ui/icons/Add'
import SearchIcon from '@material-ui/icons/Search'
import MyButton from '../../../../control/button/button'
import MyTextfield from '../../../../control/textfield/textfield'
import axios, { CancelTokenSource } from 'axios'
import { ProjectRequest, ProjectSearchFn } from '../agendaTypes'
import { useForm } from 'react-final-form'
import CustomPagination from '../../../report/closed/CustomPagination'
import CustomNoRowsOverlay from '../../../report/closed/CustomNoRowsOverlay'
import useTranslate from '../../../../i18n/useTranslate'
import useWebService from '../../../../services/web/useWebService'
import { buildReviewSummary } from '../utils/serialization'

const DEBOUNCE_MS = 400
const FIELD_NAME = '__modal_keyword'
const ROWS_PER_PAGE = 10

const ProjectPicker: React.FC<{
    label: string
    canEdit?: boolean
    onPicked: (items: any[]) => void
    searchTbl: ProjectSearchFn
}> = ({ label, canEdit = true, onPicked, searchTbl }) => {
    const classes = useStyles()
    const t = useTranslate()
    const { getProjectReviewSummary } = useWebService()
    const [confirming, setConfirming] = React.useState(false)
    const form = useForm()
    const [open, setOpen] = React.useState(false)
    const [loading, setLoading] = React.useState(false)
    const [items, setItems] = React.useState<any[]>([])
    const [keyword, setKeyword] = React.useState('')
    const [resetNonce, setResetNonce] = React.useState(0)
    const [page, setPage] = React.useState(0)
    const [total, setTotal] = React.useState(0)
    /** เก็บ "ตัวโครงการ" ที่เลือกไว้เอง ไม่พึ่ง selectionModel ของ DataGrid
     *  เพราะพอเปลี่ยนหน้าแล้วแถวชุดเก่าหลุดออกจาก rows ตัวที่เลือกไว้หน้าก่อนจะหายไปด้วย */
    const [picked, setPicked] = React.useState<Map<string | number, any>>(() => new Map())

    const reqSeqRef = React.useRef(0)
    const debTimerRef = React.useRef<number | undefined>(undefined)
    const cancelRef = React.useRef<CancelTokenSource | undefined>(undefined)
    const lastQueryRef = React.useRef('')

    const clampCell = React.useCallback(
        (params: any) => (
            <div className={classes.clamp} title={params.value == null ? '' : String(params.value)}>
                {params.value}
            </div>
        ),
        [classes.clamp],
    )

    const columns = React.useMemo<GridColDef[]>(
        () => [
            {
                field: 'projectNumber',
                headerName: t.get('UM.SETTING_AGENDA.AGENDA_ITEMS.COLUMNS.PROJECT_NUMBER'),
                width: 140,
                headerClassName: 'header-table',
            },
            {
                field: 'certificateNo',
                headerName: t.get('UM.SETTING_AGENDA.AGENDA_ITEMS.COLUMNS.CERTIFICATE_NO'),
                width: 150,
                headerClassName: 'header-table',
            },
            {
                field: 'name',
                renderCell: clampCell,
                headerName: t.get('UM.SETTING_AGENDA.AGENDA_ITEMS.COLUMNS.PI_FULL_NAME'),
                width: 260,
                headerClassName: 'header-table',
            },
            {
                field: 'projectType',
                headerName: t.get('UM.SETTING_AGENDA.AGENDA_ITEMS.COLUMNS.PROJECT_TYPE'),
                width: 170,
                headerClassName: 'header-table',
            },
            {
                field: 'projectNameTH',
                renderCell: clampCell,
                headerName: t.get('UM.SETTING_AGENDA.AGENDA_ITEMS.COLUMNS.TITLE_TH'),
                flex: 1,
                minWidth: 360,
                headerClassName: 'header-table',
            },
            {
                field: 'projectNameEN',
                renderCell: clampCell,
                headerName: t.get('UM.SETTING_AGENDA.AGENDA_ITEMS.COLUMNS.TITLE_EN'),
                flex: 1,
                minWidth: 360,
                headerClassName: 'header-table',
            },
        ].map((c) => ({ ...c, sortable: false })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t.languageStore?.currentLanguage, clampCell],
    )

    const isTh = (t.languageStore?.currentLanguage ?? 'th') !== 'en'

    const toGridRows = (list: any[], isTh: boolean) =>
        list.map((it: any, idx: number) => ({
            id: it?.projectID ?? it?.id ?? `row-${idx}`,
            projectNumber: it?.projectNumber ?? it?.projectCode ?? it?.projectNo ?? it?.recNumber ?? '-',
            certificateNo: it?.certificateLabel ?? it?.coaNo ?? '-',
            name: (isTh ? it?.researcherName_th : it?.researcherName_en) || '-',
            projectType: (isTh ? it?.consider_type_name_th : it?.consider_type_name_en) || '-',
            projectNameTH: it?.projectName_th ?? it?.nameTh ?? '-',
            projectNameEN: it?.projectName_en ?? it?.nameEn ?? '-',
            __raw: it,
        }))

    // แปลงเป็นแถวของตารางตอน render ไม่ใช่ตอนโหลด สลับภาษาระหว่างเปิด dialog ค้างไว้จะเปลี่ยนตาม
    const rows = React.useMemo(() => toGridRows(items, isTh), [items, isTh])

    const searchTblRef = React.useRef(searchTbl)
    React.useEffect(() => {
        searchTblRef.current = searchTbl
    })

    const abortInFlight = React.useCallback((reason = 'new search') => {
        cancelRef.current?.cancel(reason)
        cancelRef.current = undefined
    }, [])

    const doSearch = React.useCallback(async (q: string, pageIndex: number) => {
        abortInFlight()
        if (!q) {
            setItems([])
            setTotal(0)
            return
        }
        const seq = ++reqSeqRef.current
        const source = axios.CancelToken.source()
        cancelRef.current = source
        setLoading(true)
        try {
            const payload: ProjectRequest = {
                pageNumber: pageIndex + 1,
                rowsPerPage: ROWS_PER_PAGE,
                keywordSearch: q,
            }
            const res: any = await searchTblRef.current(payload, { cancelToken: source.token })
            if (seq !== reqSeqRef.current) return
            setItems(res?.result?.items ?? [])
            setTotal(Number(res?.result?.totalRecords ?? 0))
        } catch (err) {
            if (axios.isCancel(err)) return
            console.error('search project for meeting failed:', err)
            if (seq === reqSeqRef.current) {
                setItems([])
                setTotal(0)
            }
        } finally {
            if (seq === reqSeqRef.current) {
                setLoading(false)
                cancelRef.current = undefined
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const clearAll = React.useCallback(() => {
        setKeyword('')
        setItems([])
        setTotal(0)
        setPage(0)
        setPicked(new Map())
        lastQueryRef.current = ''

        reqSeqRef.current++
        abortInFlight('clear')
        if (debTimerRef.current) {
            window.clearTimeout(debTimerRef.current)
            debTimerRef.current = undefined
        }

        form.change(FIELD_NAME, '')

        setResetNonce((n) => n + 1)
    }, [form, abortInFlight])

    React.useEffect(() => () => abortInFlight('unmount'), [abortInFlight])

    const openModal = () => {
        setOpen(true)
    }

    const closeModal = () => {
        setOpen(false)
        clearAll()
    }

    const pageIds = React.useMemo(() => rows.map((r) => r.id), [rows])
    const selectionModel = React.useMemo(() => pageIds.filter((id) => picked.has(id)), [pageIds, picked])

    const handleSelectionChange = React.useCallback(
        (model: any) => {
            const sel = new Set(model as (string | number)[])
            setPicked((prev) => {
                const next = new Map(prev)
                rows.forEach((r) => {
                    if (sel.has(r.id)) next.set(r.id, r.__raw)
                    else next.delete(r.id)
                })
                return next
            })
        },
        [rows],
    )

    const handleConfirm = async () => {
        const pickedItems = Array.from(picked.values())
        const lang = t.languageStore?.currentLanguage ?? 'th'

        setConfirming(true)
        try {
            const ids = pickedItems.map((it: any) => Number(it?.projectID)).filter((n: number) => Number.isFinite(n))
            if (ids.length) {
                const res: any = await getProjectReviewSummary(ids)
                const byProject = new Map<number, any[]>()
                ;(res?.result ?? []).forEach((row: any) => {
                    const list = byProject.get(row.projectID) ?? []
                    list.push(row)
                    byProject.set(row.projectID, list)
                })
                pickedItems.forEach((it: any) => {
                    it.reviewSummaryText = buildReviewSummary(byProject.get(Number(it?.projectID)) ?? [], lang)
                })
            }
        } catch (ex) {
            // ปล่อยผ่าน ให้เลือกโครงการได้ตามปกติ
        } finally {
            setConfirming(false)
        }

        onPicked(pickedItems)
        closeModal()
    }

    React.useEffect(() => {
        setPage(0)
    }, [keyword])

    React.useEffect(() => {
        if (!open) return
        const q = keyword.trim()

        if (q === '') {
            setItems([])
            setTotal(0)
            lastQueryRef.current = ''
            if (debTimerRef.current) {
                window.clearTimeout(debTimerRef.current)
                debTimerRef.current = undefined
            }
            return
        }

        const delay = lastQueryRef.current === q ? 0 : DEBOUNCE_MS

        if (debTimerRef.current) window.clearTimeout(debTimerRef.current)
        debTimerRef.current = window.setTimeout(() => {
            lastQueryRef.current = q
            void doSearch(q, page)
        }, delay) as unknown as number

        return () => {
            if (debTimerRef.current) {
                window.clearTimeout(debTimerRef.current)
                debTimerRef.current = undefined
            }
        }
    }, [keyword, page, open, doSearch])

    return (
        <>
            <MyButton startIcon={<AddIcon />} type="button" color="primary" disabled={!canEdit} onClick={openModal}>
                {label}
            </MyButton>

            <Dialog key={`dlg-${resetNonce}`} open={open} onClose={closeModal} fullWidth maxWidth="xl">
                <DialogTitle disableTypography className={classes.modalHeader}>
                    <Typography variant="h6">{t.get('UM.SETTING_AGENDA.AGENDA_ITEMS.SEARCH_PROJECT')}</Typography>
                </DialogTitle>

                <DialogContent className={classes.modalContent}>
                    <Box>
                        <MyTextfield
                            key={`kw-${resetNonce}`}
                            name={FIELD_NAME}
                            resetButtom
                            icon={<SearchIcon color="disabled" />}
                            handleValueChange={(v: any) => {
                                const next = typeof v === 'string' ? v : (v?.target?.value ?? '')
                                setKeyword(next)
                            }}
                            materialInputProps={{
                                label: t.get('UM.SETTING_AGENDA.AGENDA_ITEMS.SEARCH_PROJECT'),
                                placeholder: t.get('UM.SETTING_AGENDA.AGENDA_ITEMS.SEARCH_PROJECT'),
                            }}
                        />
                    </Box>

                    <div style={{ width: '100%' }}>
                        <DataGrid
                            className={classes.dataGrid}
                            autoHeight
                            rowHeight={78}
                            rows={rows}
                            columns={columns}
                            loading={loading}
                            pagination
                            paginationMode="server"
                            rowCount={total}
                            page={page}
                            onPageChange={(p: number) => setPage(p)}
                            pageSize={ROWS_PER_PAGE}
                            rowsPerPageOptions={[ROWS_PER_PAGE]}
                            disableColumnMenu
                            disableSelectionOnClick
                            checkboxSelection
                            selectionModel={selectionModel}
                            onSelectionModelChange={handleSelectionChange}
                            components={{
                                Pagination: CustomPagination,
                                NoRowsOverlay: CustomNoRowsOverlay,
                            }}
                        />
                    </div>
                </DialogContent>

                <DialogActions style={{ padding: 16 }}>
                    <Button onClick={closeModal} variant="outlined" color="default">
                        {t.get('BUTTON.CANCEL')}
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        color="primary"
                        disabled={picked.size === 0 || confirming}
                    >
                        {t.get('BUTTON.CONFIRM')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

const useStyles = makeStyles((theme) => ({
    modalHeader: {
        background: '#D45685',
        color: '#fff',
        padding: theme.spacing(2),
    },
    modalContent: { paddingTop: theme.spacing(2) },
    clamp: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        overflow: 'hidden',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        lineHeight: 1.4,
        width: '100%',
    },
    dataGrid: {
        width: '100%',
        marginTop: 16,
        overflowX: 'auto',
        '& .MuiDataGrid-cell': {
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: '1.4 !important',
            // แถวสูงคงที่ 78px แต่บางคอลัมน์มีบรรทัดเดียว ต้องกลางแนวตั้ง
            // ไม่งั้นข้อความไปติดขอบบนเมื่อคอลัมน์อื่นตัดเป็น 2-3 บรรทัด
            display: 'flex',
            alignItems: 'center',
            paddingTop: 8,
            paddingBottom: 8,
            overflow: 'hidden',
        },
        '& .MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'normal',
            lineHeight: 1.25,
        },
        '& .header-table': {
            backgroundColor: '#DB5F8E',
            color: '#FFFFFF',
            fontWeight: 700,
        },
        '& .MuiDataGrid-columnSeparator': { visibility: 'hidden' },
        '& .MuiDataGrid-iconSeparator': { display: 'none' },
        '& .MuiDataGrid-sortIcon': { color: '#FFFFFF' },
        '& .MuiDataGrid-columnHeaderWrapper': {
            backgroundColor: '#DB5F8E',
            '& .MuiCheckbox-root': { color: '#FFFFFF' },
            '& .MuiCheckbox-root.Mui-checked': { color: '#FFFFFF' },
        },
        '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
    },
}))

export default React.memo(ProjectPicker)
